import React, { useState } from 'react';
import { track } from './track';

/**
 * Share, placed at the moment someone learns what the panel actually decided.
 *
 * That moment is the whole reason this works: the surprise is the thing people want to pass
 * on, and until now it ended in an email box and nothing else.
 *
 * ⚠️ THE SHARE TEXT NEVER CARRIES THE OUTCOME. A sharer who spoils the panel's decision
 * ruins the survey for everyone they send it to — they arrive knowing the answer, so their
 * response is worthless and the comparison is polluted. The hook only.
 *
 * ⚠️ AND EACH LINK CARRIES ?src. Without it we cannot tell a Facebook respondent from a
 * LinkedIn one, which is the difference between a general-public sample and a professional
 * one — and that distinction is the whole claim the study makes.
 */
const URL = 'https://research.sequentus.org';
const HOOK = 'I just spent three minutes on this. You read a real case where a professional '
  + 'faced disciplinary proceedings, say what you think should happen, and then find out what '
  + 'the panel actually decided.';

const link = (src: string) => `${URL}/?src=${src}`;

const TARGETS = [
  { key: 'wa', label: 'WhatsApp',
    href: () => `https://wa.me/?text=${encodeURIComponent(`${HOOK} ${link('wa')}`)}` },
  { key: 'fb', label: 'Facebook',
    href: () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link('fb'))}` },
  { key: 'li', label: 'LinkedIn',
    href: () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link('li'))}` },
  { key: 'em', label: 'Email',
    href: () => `mailto:?subject=${encodeURIComponent('What would you do?')}&body=${encodeURIComponent(`${HOOK}\n\n${link('em')}`)}` },
];

export default function Share() {
  const [copied, setCopied] = useState(false);

  async function copy() {
    track('research_share', { channel: 'copy' });
    try {
      await navigator.clipboard.writeText(link('cp'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard is blocked in plenty of contexts. Say so rather than showing
      // a "copied!" for something that did not copy.
      window.prompt('Copy this link', link('cp'));
    }
  }

  return (
    <div className="share">
      <p className="share-lead">Surprised? Send it to someone and see what they say.</p>
      <div className="share-row">
        {TARGETS.map(t => (
          <a key={t.key} className="share-btn" href={t.href()} target="_blank" rel="noopener noreferrer"
             onClick={() => track('research_share', { channel: t.key })}>{t.label}</a>
        ))}
        <button className="share-btn" onClick={copy}>{copied ? 'Link copied' : 'Copy link'}</button>
      </div>
    </div>
  );
}
